import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useMemo } from 'react';
import { LegacyDevice } from 'console/device/device';
import { usePartialAppSelector } from 'shared/hooks/redux/hooks';
import { DeviceUISettingsProvider } from '../device-ui-settings';
import { getVehicleThermometersConfig } from '../../content/device/thermometers/vehicle';
import { VehicleDeviceStateProvider } from './state';
import { VehicleDeviceConfigProvider } from './config';
import { getLegacyGuardControlsConfig } from '../../content/device/guard-control/legacy';
import { Capability } from 'console/device/device-capability';
import { getLegacyCustomControls } from '../../content/device/custom-controls/legacy';
import { legacy_custom_controls_config } from '../../content/device/custom-controls/legacy/utils';
import { getVehicleRfIndicators } from '../../content/device/rf-indicators/vehicle';
import { getVehicleControlsConfig } from '../../content/device/vehicle-controls';
import { createSubsetSelector } from 'webui/utils/selectors';
import { createSelector } from '@reduxjs/toolkit';
import { getTabConfig } from '../utils';
import { legacy_guard_controls_config } from '../../content/device/guard-control/legacy/utils';
import { vehicle_thermometers_config } from '../../content/device/thermometers/vehicle/utils';
import { vehicle_controls_config } from '../../content/device/vehicle-controls/utils';
import { vehicle_rf_indicators_config } from '../../content/device/rf-indicators/vehicle/utils';
import { selectDevice } from 'entities/device/lib';
import { useDeviceConfig } from '../device/config';
import { useSupportAccount } from '../../hooks/helpers';
const vehicle_device_selector = createSelector(selectDevice, createSubsetSelector([
    'id',
    'silent_mode',
    'service_mode',
    'car_view',
    'firmware_version',
    'voltage',
    'radiosilence',
    'auto_ignition',
    'balance_value',
]));
const select_custom_controls_device = createSelector(selectDevice, legacy_custom_controls_config);
const select_thermometers_device = createSelector(selectDevice, vehicle_thermometers_config);
const select_vehicle_controls_device = createSelector(selectDevice, vehicle_controls_config);
const select_guard_controls_device = createSelector(selectDevice, legacy_guard_controls_config);
const select_rf_indicators_device = createSelector(selectDevice, vehicle_rf_indicators_config);
export const VehicleDeviceProvider = memo(function VehicleDeviceProvider(props) {
    const { children } = props;
    const { has_guard_control, capabilities, device_id } = useDeviceConfig();
    const isSupportAccount = useSupportAccount();
    const vehicle_device = usePartialAppSelector(vehicle_device_selector, device_id);
    const { silent_mode, service_mode, car_view, firmware_version, voltage, radiosilence, auto_ignition, } = useMemo(() => {
        if (vehicle_device) {
            return vehicle_device;
        }
        return {};
    }, [vehicle_device]);
    const { has_vehicle_controls, has_vehicle_sensors, support_vehicle_controls } = useMemo(() => {
        const has_vehicle_controls = LegacyDevice.has_capability(capabilities, Capability.has_car_controls);
        const has_vehicle_sensors = LegacyDevice.has_capability(capabilities, Capability.has_car_sensors);
        const support_vehicle_controls = has_vehicle_sensors || has_vehicle_controls;
        return {
            has_vehicle_controls,
            has_vehicle_sensors,
            support_vehicle_controls,
        };
    }, [capabilities]);
    const custom_controls_device = usePartialAppSelector(select_custom_controls_device, device_id);
    const guard_control_device = has_guard_control
        ? usePartialAppSelector(select_guard_controls_device, device_id)
        : null;
    const vehicle_controls_device = support_vehicle_controls
        ? usePartialAppSelector(select_vehicle_controls_device, device_id)
        : null;
    const thermometers_device = usePartialAppSelector(select_thermometers_device, device_id);
    const rf_indicators_device = usePartialAppSelector(select_rf_indicators_device, device_id);
    const vehicle_custom_controls = useMemo(() => custom_controls_device && getLegacyCustomControls(custom_controls_device), [custom_controls_device]);
    const thermometers = useMemo(() => thermometers_device && getVehicleThermometersConfig(thermometers_device), [thermometers_device]);
    const guard_controls = useMemo(() => guard_control_device
        ? getLegacyGuardControlsConfig(guard_control_device)
        : null, [has_guard_control, guard_control_device]);
    const vehicle_controls = useMemo(() => vehicle_controls_device
        ? getVehicleControlsConfig(vehicle_controls_device)
        : null, [support_vehicle_controls, vehicle_controls_device]);
    const rf_indicators = useMemo(() => rf_indicators_device && getVehicleRfIndicators(rf_indicators_device), [rf_indicators_device]);
    const device_tab_content = useMemo(() => getTabConfig({
        vehicle_controls,
        thermometers,
        guard_controls,
        vehicle_custom_controls,
        rf_indicators,
    }), [
        vehicle_controls,
        thermometers,
        guard_controls,
        vehicle_custom_controls,
        rf_indicators,
    ]);
    const is_support_service_mode = useMemo(() => {
        if (firmware_version?.[1]) {
            return firmware_version?.[1] >= 387;
        }
        return false;
    }, [firmware_version]);
    const has_gsm_balance = useMemo(() => LegacyDevice.has_capability(capabilities, Capability.has_gsm), [capabilities]);
    return (_jsx(VehicleDeviceConfigProvider, { car_view: car_view, radiosilence: radiosilence, has_vehicle_sensors: has_vehicle_sensors, has_vehicle_controls: has_vehicle_controls, has_gsm_balance: has_gsm_balance, firmware_version: firmware_version, is_support_service_mode: is_support_service_mode, children: _jsx(VehicleDeviceStateProvider, { silent_mode: silent_mode, voltage: voltage, service_mode: service_mode ?? false, auto_ignition: auto_ignition, children: _jsx(DeviceUISettingsProvider, { device_config: device_tab_content, children: children }) }) }));
});
